<template>
    <section :class="['token-price-chart', { 'token-price-chart--advanced': isAdvanced }]">
        <div v-if="isAdvanced"
            class="active-point"
        >
            <div class="active-point__title">{{ formattedActivePoint.title }}</div>
            <div class="active-point__value">{{ formattedActivePoint.price }}</div>
            <div class="active-point__date">{{ formattedActivePoint.date }}</div>
        </div>

        <canvas id="myChart"
            @mouseleave="() => currentActivePoint = {}"
        />

        <div v-if="isAdvanced"
            class="range-selectors"
        >
            <div v-for="(period, index) in periods"
                :key="index"
                :class="['range-selectors__selector', { 'range-selectors__selector--active': period.isActive}]"
                @click="onPeriodClick(index)"
            >
                {{ period.text }}
            </div>
        </div>

        <loading-spinner v-if="isLoading" />
    </section>
</template>

<script>
// 3rd party
import Chart from 'chart.js';

import {
    format,
    addDays,
    subDays,
    subHours,
    subMonths,
    setSeconds,
    subYears,
} from '@shared/utils/dates';

// Utils
import {
    formatPrice,
} from '@shared/utils/formatter.js';

// Store
import {
    mapActions,
    mapGetters,
} from 'vuex';
import localEpicTypes from '@webapp/epics/types.js';

// Components
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';

export default {
    name: 'token-price-chart',
    components: {
        loadingSpinner,
    },
    props: {
        marketId: {
            type: String,
            required: true,
        },
        currentPrice: {
            type: Object,
            default: () => ({}),
        },
        isAdvanced: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            currentActivePoint: {},
            isLoading: true,
            periods: [
                {
                    text: '1D',
                    range: 'mins',
                    // Do not fetch same time yesterday because the graph breaks otherwise (same x values, eg. 11:15 for yday and 11:15 for today)
                    hours: 23.75,
                    isActive: false,
                },
                {
                    text: '1W',
                    range: 'days',
                    hours: 24 * 7,
                    isActive: false,
                },
                {
                    text: '1M',
                    range: 'days',
                    hours: 24 * 7 * 30,
                    isActive: true,
                },
                {
                    text: '1Y',
                    range: 'days',
                    hours: 24 * 7 * 365,
                    isActive: false,
                },
                {
                    text: 'ALL',
                    range: 'days',
                    hours: 0,
                    isActive: false,
                },
            ],
            myChart: {},
        };
    },
    computed: {
        ...mapGetters([
            'defaultCurrency',
            'getCurrencyShortCodeFormatted',
        ]),

        ...mapGetters('marketplace', ['periodicStats']),

        formattedActivePoint() {
            if (Object.keys(this.currentActivePoint).length) {
                return {
                    title: this.$t('partner.selectedMarketPrice'),
                    date: format(new Date(this.currentActivePoint.startDate), 'MMM dd, yyyy'),
                    price: `${this.formatPrice(this.currentActivePoint.closePrice)} ${this.getCurrencyShortCodeFormatted(this.defaultCurrency.id)}`,
                };
            }

            // If the data is loaded but nothing is hovered on yet, show the Current Price
            if (Object.keys(this.currentPrice).length) {
                return {
                    title: this.$t('partner.currentMarketPrice'),
                    date: format(Date.now(), 'MMM dd, yyyy'),
                    price: this.currentPrice.value,
                };
            }

            return {
                title: this.$t('partner.currentMarketPrice'),
                date: '--',
                price: '--',
            };
        },
    },
    watch: {
        periodicStats(chartData) {
            if (chartData.length) {
                this.renderChart(chartData, this);
            }
        },
    },
    mounted() {
        this.loadChartData();
    },
    methods: {
        ...mapActions({
            loadPeriodicStats: localEpicTypes.GET_PERIODIC_STATS,
        }),

        loadChartData() {
            if (!this.marketId) {
                return null;
            }

            const period = this.getDataPeriod();
            const activePeriod = this.getActivePeriod();
            const dateTime = this.getUTCDateTime();
            const fromTo = activePeriod && activePeriod.hours ? 'date[from]' : 'date[to]';

            this.loadPeriodicStats({
                period,
                marketId: this.marketId,
                [fromTo]: dateTime,
            });
        },

        renderChart(data, vm) {
            // If the chart is already initialised, destroy it to start a fresh chart
            if (Object.keys(this.myChart).length) {
                this.myChart.destroy();
            }

            const dataLabels = data.map((x) => this.formatDate(x.startDate)),
                dataPoints = data.map((x) => x.closePrice);

            // Extend the default chart and create a hovering mechanism
            Chart.defaults.LineWithLine = Chart.defaults.line;
            Chart.controllers.LineWithLine = Chart.controllers.line.extend({
                draw: function(ease) {
                    Chart.controllers.line.prototype.draw.call(this, ease);

                    if (this.chart.tooltip._active && this.chart.tooltip._active.length) { // eslint-disable-line
                        const activePoint = this.chart.tooltip._active[0], // eslint-disable-line
                            bottomY = this.chart.scales['y-axis-0'].bottom,
                            ctx = this.chart.ctx,
                            topY = this.chart.scales['y-axis-0'].top,
                            x = activePoint.tooltipPosition().x;

                        // Get the data related to the current activePoint and store it
                        vm.currentActivePoint = data[activePoint._index]; // eslint-disable-line

                        // Draw line
                        ctx.save();
                        ctx.beginPath();
                        ctx.moveTo(x, topY);
                        ctx.lineTo(x, bottomY);
                        ctx.lineWidth = 1;
                        ctx.strokeStyle = '#113d49';
                        ctx.stroke();
                        ctx.restore();
                    }
                },
            });

            const canvas = document.getElementById('myChart');
            const ctx = canvas.getContext('2d');

            // Using height * 2 to smoothen the transition between the two colors
            const gradientFill = ctx.createLinearGradient(canvas.width / 2, 0, canvas.width / 2, canvas.height * 2);

            gradientFill.addColorStop(0, 'rgb(141, 161, 167)');

            if (this.isAdvanced) {
                gradientFill.addColorStop(1, 'rgba(141, 161, 167, 0.2)');
            } else {
                gradientFill.addColorStop(1, 'rgb(255, 255, 255)');
            }

            this.myChart = new Chart(ctx, { // eslint-disable-line
                type: 'LineWithLine',
                data: {
                    labels: dataLabels,
                    datasets: [
                        {
                            label: 'Close Price',
                            borderColor: '#113d49',
                            pointBorderColor: '#113d49',
                            pointBackgroundColor: '#113d49',
                            pointHoverBackgroundColor: '#113d49',
                            pointHoverBorderColor: '#113d49',
                            pointBorderWidth: 0,
                            pointHoverRadius: 0,
                            pointHoverBorderWidth: 0,
                            pointRadius: 0,
                            borderWidth: 2,
                            backgroundColor: gradientFill,
                            data: dataPoints,
                        },
                    ],
                },
                options: {
                    legend: {
                        display: false,
                    },
                    layout: {
                        padding: {
                            top: 1,
                        },
                    },
                    tooltips: {
                        axis: 'x',
                        intersect: false,
                        callbacks: {
                            label: function(tooltipItem) {
                                return vm.$t('marketplace.trading.closePrice') + ': ' + vm.formatPrice(tooltipItem.value);
                            },
                        },
                    },
                    scales: {
                        xAxes: [
                            {
                                display: false,
                            },
                        ],
                        yAxes: [
                            {
                                display: false,
                            },
                        ],
                    },
                },
            });

            this.$nextTick(() => {
                this.isLoading = false;
            });
        },

        getUTCDateTime() {
            const activePeriod = this.getActivePeriod(),
                activePeriodText = activePeriod && activePeriod.text;

            let finalDate = new Date();

            switch (activePeriodText) {
                case '1W':
                    finalDate = subDays(finalDate, 7);
                    break;

                case '1M':
                    finalDate = subMonths(finalDate, 1);
                    break;

                case '1Y':
                    finalDate = subYears(finalDate, 1);
                    break;

                case 'ALL':
                    // Add 1 day since we need today to be displayed
                    finalDate = addDays(finalDate, 1);
                    break;

                default:
                    finalDate = subHours(finalDate, activePeriod.hours);
                    break;
            }

            return format(setSeconds(finalDate, 0), 'standardDateTime');
        },

        setActivePeriod(periodKey) {
            this.periods = this.periods.map((item) => ({
                ...item,
                isActive: false,
            }));

            if (this.periods[periodKey]) {
                this.periods[periodKey].isActive = true;
            }
        },

        getActivePeriod() {
            return this.periods.find((period) => Boolean(period.isActive));
        },

        getDateFormat() {
            const activePeriod = this.getActivePeriod();

            return activePeriod && activePeriod.range === 'days' ? 'dateShort' : 'time';
        },

        formatDate(date) {
            return format(new Date(date), this.getDateFormat());
        },

        getDataPeriod() {
            const activePeriod = this.getActivePeriod();

            if (!activePeriod || activePeriod.range === 'days') {
                return 'PT24H';
            }

            return 'PT15M';
        },

        onPeriodClick(periodKey) {
            this.isLoading = true;
            this.setActivePeriod(periodKey);
            this.loadChartData();
        },

        formatPrice,
    },
};
</script>

<style lang="scss" scoped>
.token-price-chart {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 0.5rem;
    width: 100%;
    color: $cyprus-color;
    position: relative;

    .active-point {
        display: flex;
        text-align: center;
        margin: 1.5rem 0;
        flex-direction: column;
        align-items: center;

        &__title {
            font-size: 1.5rem;
            font-weight: bold;
        }

        &__value {
            padding: 0.5rem 1.5rem;
            margin: 0.5rem 0;
            border-radius: 5rem;
            border: 0.2rem solid $concrete-color;
            font-size: 2rem;
            font-weight: bold;
            background: $white-color;
        }

        &__date {
            font-size: 1.2rem;
        }
    }

    .range-selectors {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        padding: 2.5rem 0;

        &__selector {
            padding: 0.5rem 1rem;
            margin: 0 1.5rem;
            border-radius: 1.5rem;
            font-size: 1.2rem;

            @include min-width(xl) {
                margin: 0 2.5rem;
            }

            &--active,
            &:hover {
                cursor: pointer;
                color: $white-color;
                font-weight: bold;
                background-color: $cyprus-color;
            }
        }
    }

    .spinner {
        position: absolute;
        width: 100%;
        height: 100%;
        background: rgba($athens-gray-color, 0.7);
        z-index: 1;
    }

    .chartjs-render-monitor {
        z-index: 1;
        background: $white-color;
    }

    &--advanced {
        background-color: rgba($athens-gray-color, 0.3);

        .chartjs-render-monitor {
            background-color: unset;
            border-bottom: 0.1rem dashed rgba($cyprus-color, 0.3);
        }
    }
}
</style>